import { useQuery } from '@tanstack/react-query';
import { adminService, organizationService } from '@/services/api';
import { Spinner } from '@/components/ui/spinner';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { HowItWorksFlow } from '@/components/tips';
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  DollarSign,
  ExternalLink,
  Link2,
  Plus,
  Share2,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { Link, useLocation } from 'wouter';
import { cn, formatCurrency } from '@/lib/utils';
import { getPublicBookingUrl } from '@/utils/subdomain';
import { type BookingStatus } from '@/lib/status-colors';
import { useCopyFlash } from '@/components/patterns/CopiedFlash';
import type { Booking } from '@/types/booking';
import { ShareBookingLink } from '@/components/ShareBookingLink';
import { KpiTile } from '@/components/patterns/KpiTile';
import { TopServicesList } from '@/components/patterns/TopServicesList';

export default function AdminDashboardPage() {
  const { t } = useTranslation(['dashboard', 'common', 'tips']);

  useSetPageHeader({ sub: t('dashboard:subtitle') }, [t]);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => adminService.getDashboardStats(),
  });

  const { data: recentBookings, isLoading: bookingsLoading } = useQuery({
    queryKey: ['recent-bookings'],
    queryFn: () => adminService.getRecentBookings(5),
  });

  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: () => organizationService.getOrganization(),
  });

  if (statsLoading || bookingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const bookingUrl = organization?.subdomain
    ? getPublicBookingUrl(organization.subdomain)
    : '';

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      {/* The share link comes first: it is how clients reach the business. */}
      {bookingUrl && (
        <BookingLinkCard
          bookingUrl={bookingUrl}
          businessName={organization?.name || organization?.business_name || ''}
        />
      )}

      {/* How Sloty Works - compact visual flow diagram (dismissible) */}
      <HowItWorksFlow compact dismissible />

      {/* Phones read top to bottom: upcoming bookings, stats (collapsed), top
          services. The desktop puts the stats row first. */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 lg:row-start-2">
          <UpcomingBookings bookings={recentBookings ?? []} />
        </div>

        <div className="lg:col-span-3 lg:row-start-1">
          <StatsSection
            today={stats?.todayBookings ?? 0}
            pending={stats?.pendingBookings ?? 0}
            revenue={formatCurrency(stats?.monthlyRevenue || 0, (organization?.currency || 'USD') as any)}
            total={stats?.totalBookings ?? 0}
          />
        </div>

        <div className="lg:row-start-2">
          <TopServicesCard services={stats?.topServices ?? []} />
        </div>
      </div>
    </div>
  );
}

const STATS_OPEN_KEY = 'sloty.dashboard.statsOpen';

function readStatsOpen() {
  try {
    return window.localStorage.getItem(STATS_OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

function UpcomingBookings({ bookings }: { bookings: Booking[] }) {
  const { t } = useTranslation(['dashboard']);
  const [, setLocation] = useLocation();

  return (
    <section className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card" aria-labelledby="upcoming-bookings">
      <header className="flex items-center justify-between gap-3 px-4 py-3.5 sm:px-6 sm:py-4">
        <div className="min-w-0">
          <h2 id="upcoming-bookings" className="text-h3 text-ink">{t('dashboard:upcomingBookings.title')}</h2>
          <p className="hidden text-body-sm text-soft sm:block">{t('dashboard:upcomingBookings.subtitle')}</p>
        </div>
        <Link href="/admin/bookings" className="inline-flex min-h-touch-target shrink-0 items-center gap-1 text-sm font-semibold text-brand-700 hover:text-brand-900">
          {t('dashboard:upcomingBookings.viewAll')}
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </header>

      {bookings.length === 0 ? (
        <div className="border-t border-line px-4 py-10 text-center">
          <Calendar className="mx-auto mb-3 h-10 w-10 text-soft" aria-hidden="true" />
          <p className="text-body-sm text-soft">{t('dashboard:upcomingBookings.noBookings')}</p>
          <Button onClick={() => setLocation('/admin/bookings/new')} className="mt-4" size="sm">
            <Plus className="mr-2 h-4 w-4" />
            {t('dashboard:upcomingBookings.createBooking')}
          </Button>
        </div>
      ) : (
        <ul className="m-0 list-none p-0">
          {bookings.map((booking) => (
            <UpcomingRow key={booking.id} booking={booking} />
          ))}
        </ul>
      )}
    </section>
  );
}

function UpcomingRow({ booking }: { booking: Booking }) {
  const { t } = useTranslation(['dashboard', 'common']);
  // Handle both camelCase and snake_case from API
  const availableSlot = booking.availableSlot || (booking as any).available_slot;
  const startTime: string | undefined = availableSlot?.startTime || availableSlot?.start_time;
  const serviceName = availableSlot?.service?.name || booking.service?.name;
  const providerName = availableSlot?.provider?.displayName || availableSlot?.provider?.name || booking.provider?.name;
  const customerName = [booking.customerFirstName, booking.customerLastName].filter(Boolean).join(' ');
  const start = startTime ? new Date(startTime) : null;
  // Always the date: "Tomorrow"/"Heute" don't fit the narrow time column.
  const day = start
    ? format(start, getDateFormats().dayMonth, { locale: getDateLocale() })
    : t('dashboard:upcomingBookings.noDate');

  return (
    <li className="border-t border-line">
      <Link
        href={`/admin/bookings/${booking.publicId}`}
        className="flex items-center gap-3 px-4 py-3 text-ink transition-colors hover:bg-surface-2 sm:px-6"
      >
        <div className="w-16 shrink-0 self-start">
          <div className="text-[15px] font-semibold tabular-nums">{start ? format(start, 'HH:mm') : '–'}</div>
          <div className="whitespace-nowrap text-xs text-soft">{day}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{customerName || serviceName}</div>
          {customerName && serviceName && <div className="truncate text-[13px] text-soft">{serviceName}</div>}
          {providerName && (
            <div className="truncate text-[13px] text-soft">
              {t('dashboard:upcomingBookings.withProvider', { name: providerName })}
            </div>
          )}
        </div>
        {/* Confirmed is the normal case; only flag the others. */}
        {booking.status !== 'confirmed' && (
          <StatusBadge status={booking.status as BookingStatus} className="shrink-0">
            {t(`dashboard:status.${booking.status}`)}
          </StatusBadge>
        )}
        <ChevronRight className="h-4 w-4 shrink-0 text-text-muted" aria-hidden="true" />
      </Link>
    </li>
  );
}

interface StatsSectionProps {
  today: number;
  pending: number;
  revenue: string;
  total: number;
}

function StatsSection({ today, pending, revenue, total }: StatsSectionProps) {
  const { t } = useTranslation(['dashboard']);
  const [open, setOpen] = useState(readStatsOpen);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      window.localStorage.setItem(STATS_OPEN_KEY, next ? '1' : '0');
    } catch {
      // Private mode or blocked storage: the toggle still works for this visit.
    }
  };

  const items = [
    { label: t('dashboard:stats.today.title'), value: today },
    { label: t('dashboard:stats.pending.title'), value: pending },
    { label: t('dashboard:stats.monthlyRevenue.title'), value: revenue },
    { label: t('dashboard:stats.totalBookings.title'), value: total },
  ];

  return (
    <>
      {/* Phones: one summary row that expands into the four numbers. */}
      <section className="rounded-2xl border border-line bg-surface shadow-card sm:hidden">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls="dashboard-stats"
          aria-label={open ? t('dashboard:stats.hide') : t('dashboard:stats.show')}
          className="flex min-h-touch-target w-full items-center gap-3 px-4 py-3 text-left"
        >
          <span className="flex min-w-0 flex-1 flex-wrap gap-x-4 gap-y-1 text-sm">
            {items.slice(0, 3).map((item) => (
              <span key={item.label} className="whitespace-nowrap text-soft">
                {item.label} <span className="font-semibold tabular-nums text-ink">{item.value}</span>
              </span>
            ))}
          </span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-soft transition-transform', open && 'rotate-180')} aria-hidden="true" />
        </button>
        {open && (
          <dl id="dashboard-stats" className="m-0 grid grid-cols-2 border-t border-line">
            {items.map((item, i) => (
              <div key={item.label} className={cn('px-4 py-3', i % 2 === 1 && 'border-l border-line', i > 1 && 'border-t border-line')}>
                <dt className="text-xs text-soft">{item.label}</dt>
                <dd className="m-0 mt-0.5 text-xl font-semibold tabular-nums text-ink">{item.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </section>

      <div className="hidden gap-4 sm:grid sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile label={items[3].label} value={total} icon={<Calendar />} seriesTone="brand" />
        <KpiTile label={items[1].label} value={pending} icon={<Clock />} seriesTone="warn" />
        <KpiTile label={items[2].label} value={revenue} icon={<DollarSign />} seriesTone="success" />
        <KpiTile label={items[0].label} value={today} icon={<TrendingUp />} seriesTone="brand" />
      </div>
    </>
  );
}

function BookingLinkCard({ bookingUrl, businessName }: { bookingUrl: string; businessName: string }) {
  const { t } = useTranslation(['dashboard']);
  const { copy } = useCopyFlash();
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const displayUrl = bookingUrl.replace(/^https?:\/\//, '');

  const copyLink = () => {
    copy(bookingUrl);
    toast.success(t('dashboard:share.copied'));
  };

  const share = async () => {
    try {
      await navigator.share({ title: businessName, text: t('dashboard:publicBookingPage.shareText', { name: businessName }), url: bookingUrl });
    } catch {
      // Dismissed by the user: nothing to do.
    }
  };

  return (
    <section
      id="tour-booking-url"
      className="flex flex-col gap-3 rounded-2xl border border-brand-200 bg-surface p-4 shadow-card sm:p-5 lg:flex-row lg:items-center lg:gap-4"
      aria-labelledby="booking-link-title"
    >
      <div className="flex items-center gap-3 lg:w-72 lg:shrink-0">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand text-white">
          <Link2 className="h-[18px] w-[18px]" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 id="booking-link-title" className="m-0 text-base font-semibold text-ink">{t('dashboard:publicBookingPage.title')}</h2>
          <p className="m-0 text-xs text-soft">
            {t('dashboard:publicBookingPage.subtitle', 'Share this link so clients can book you online')}
          </p>
        </div>
      </div>

      <div className="flex h-11 min-w-0 items-center gap-1 rounded-xl border border-line bg-surface-2 pl-3 pr-1 lg:flex-1">
        <code className="min-w-0 flex-1 truncate font-mono text-[13px] text-ink">{displayUrl}</code>
        <button
          type="button"
          onClick={copyLink}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-soft hover:bg-surface hover:text-ink"
          aria-label={t('dashboard:publicBookingPage.copyUrl')}
          title={t('dashboard:publicBookingPage.copyUrl')}
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 lg:w-72 lg:shrink-0">
        <a
          href={bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-line bg-surface text-sm font-semibold text-ink transition-colors hover:bg-surface-2"
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
          {t('dashboard:publicBookingPage.openNewTab', 'Open')}
        </a>
        {canShare ? (
          <Button onClick={share} iconLeft={<Share2 className="h-4 w-4" />} className="h-10">
            {t('dashboard:publicBookingPage.share')}
          </Button>
        ) : (
          <div className="flex h-10 items-center justify-center rounded-lg border border-line">
            <ShareBookingLink bookingUrl={bookingUrl} businessName={businessName} compact showCopy={false} />
          </div>
        )}
      </div>
    </section>
  );
}

function TopServicesCard({ services }: { services: Array<{ id: number; name: string; count: number }> }) {
  const { t } = useTranslation(['dashboard']);
  return (
    <section className="rounded-2xl border border-line bg-surface shadow-card">
      <div className="border-b border-line px-4 py-3.5 sm:px-6 sm:py-4">
        <h2 className="text-h3 text-ink">{t('dashboard:topServices.title')}</h2>
        <p className="mt-0.5 text-body-sm text-soft">{t('dashboard:topServices.subtitle')}</p>
      </div>
      <div className="p-4">
        {services.length > 0 ? (
          <TopServicesList items={services.map((service) => ({ id: service.id, name: service.name, count: service.count }))} />
        ) : (
          <div className="py-8 text-center">
            <TrendingUp className="mx-auto mb-2 h-10 w-10 text-soft" aria-hidden="true" />
            <p className="text-body-sm text-soft">{t('dashboard:topServices.noData')}</p>
          </div>
        )}
      </div>
    </section>
  );
}
